import { ArrowLeft, Terminal, Zap, AlertTriangle } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Head } from "vite-react-ssg";
import { Card, CardContent } from "@/components/ui/card";
import NavigationButtons from "@/components/NavigationButtons";
import RelatedLinks from "@/components/RelatedLinks";
import { DEEPSEEK_PRICING } from "@/lib/deepseek-pricing";

const slashCommands = [
  { cmd: "/pro", what: "Arms DeepSeek-V4-Pro for the next turn only, then falls back to Flash." },
  { cmd: "/preset max", what: "Uses V4-Pro for the whole session — highest quality, highest cost." },
  { cmd: "/help", what: "Full slash-command reference inside the TUI." },
];

const DeepseekReasonix = () => {
  const navigate = useNavigate();
  const flash = DEEPSEEK_PRICING.models["v4-flash"];
  const pro = DEEPSEEK_PRICING.models["v4-pro"];
  const canonical = "https://deepseek.ai/blog/deepseek-reasonix-coding-agent-cli";
  const { announced, applies } = DEEPSEEK_PRICING.peakSurcharge;

  const surchargeSentence = applies
    ? "A peak-hour surcharge is currently active on the official rate card, so session cost varies by time of day."
    : announced
      ? "A peak-hour surcharge has been announced but is not active yet — the official rate card still shows one flat tier."
      : "No peak-hour surcharge is in effect; the official rate card shows one flat tier.";

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline:
      "Reasonix: The DeepSeek-Native Terminal Coding Agent — Setup, Flash-First Cost Control and Slash Commands",
    datePublished: "2026-08-02T14:00:00+00:00",
    dateModified: "2026-08-02T14:00:00+00:00",
    author: {
      "@type": "Organization",
      name: "The Independent DeepSeek Guide",
      url: "https://deepseek.ai",
    },
    publisher: {
      "@type": "Organization",
      name: "The Independent DeepSeek Guide",
      logo: { "@type": "ImageObject", url: "https://deepseek.ai/og-image.png" },
    },
    description:
      "DeepSeek's agent-integrations docs now list Reasonix, a DeepSeek-native coding agent that runs in the terminal via npx reasonix code. Install steps, how the Flash-first default keeps cost down, and when to arm V4-Pro.",
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://deepseek.ai/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://deepseek.ai/blog" },
      { "@type": "ListItem", position: 3, name: "Reasonix DeepSeek coding agent", item: canonical },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is Reasonix?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Reasonix is a DeepSeek-native coding agent that runs in your terminal. It talks to api.deepseek.com directly — without an OpenAI-compatibility translation shim — and is built around a cache-first request loop, flash-first cost control and automatic tool-call repair. DeepSeek lists it in the agent-integrations section of its official API documentation.",
        },
      },
      {
        "@type": "Question",
        name: "How do I install Reasonix for DeepSeek?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Install Node.js 20.10 or newer (Windows users also need Git for Windows), get an API key from platform.deepseek.com/api_keys, then run `cd /path/to/my-project` followed by `npx reasonix code`. No global install is required. On first run a built-in wizard prompts for the key and persists it to ~/.reasonix/config.json, so you do not need to export an environment variable.",
        },
      },
      {
        "@type": "Question",
        name: "Which DeepSeek model does Reasonix use by default?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "DeepSeek-V4-Flash. That is the cheap, fast tier, which is what makes a terminal agent affordable to run for hours of iteration. Type /pro inside the TUI to arm DeepSeek-V4-Pro for the next turn only, or /preset max to use Pro for the entire session.",
        },
      },
      {
        "@type": "Question",
        name: "What does a Reasonix session cost?",
        acceptedAnswer: {
          "@type": "Answer",
          text: `On the default Flash tier, DeepSeek-V4-Flash is $${flash.inputCacheMiss} per 1M input tokens on a cache miss, $${flash.inputCacheHit} on a cache hit and $${flash.output} per 1M output tokens. Because Reasonix is cache-first, repeated context in a long session is billed largely at the cache-hit rate. Switching to V4-Pro raises that to $${pro.inputCacheMiss} / $${pro.inputCacheHit} input and $${pro.output} output per 1M tokens. ${surchargeSentence}`,
        },
      },
      {
        "@type": "Question",
        name: "Is Reasonix made by DeepSeek?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No. Reasonix is a third-party open-source project that DeepSeek documents as a supported agent integration. DeepSeek supplies the models and the API; the agent itself is maintained independently. This guide is likewise an independent fan-run resource and is not affiliated with DeepSeek.",
        },
      },
      {
        "@type": "Question",
        name: "How is Reasonix different from using DeepSeek through Codex or Cline?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Most coding agents were written for another vendor's API and reach DeepSeek through an OpenAI-compatible endpoint. Reasonix targets api.deepseek.com natively, so it can lean on DeepSeek-specific behaviour such as context caching and its own tool-call repair rather than treating DeepSeek as a drop-in substitute.",
        },
      },
    ],
  };

  return (
    <>
      <Head>
        <title>Reasonix: DeepSeek Coding Agent in Your Terminal (2026)</title>
        <meta
          name="description"
          content="Reasonix is a DeepSeek-native terminal coding agent: npx reasonix code, V4-Flash by default, /pro to arm V4-Pro. Setup steps, cache-first cost control and real per-token rates."
        />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta
          property="og:title"
          content="Reasonix: The DeepSeek-Native Terminal Coding Agent"
        />
        <meta
          property="og:description"
          content="No translation shim, no global install. npx reasonix code runs a DeepSeek coding agent on V4-Flash by default, with /pro to escalate to V4-Pro when a task needs it."
        />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Head>

      <div className="min-h-screen bg-white">
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Button
            variant="ghost"
            onClick={() => navigate("/blog")}
            className="mb-6 text-slate-600 hover:text-[#0066FF]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>

          <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-4">
            <Link to="/" className="hover:text-[#0066FF]">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/blog" className="hover:text-[#0066FF]">Blog</Link>
            <span className="mx-2">/</span>
            <span className="text-slate-700">Reasonix DeepSeek coding agent</span>
          </nav>

          <div className="mb-6 inline-flex items-center gap-2 text-xs font-medium bg-blue-50 text-[#0066FF] border border-blue-200 px-3 py-1.5 rounded-full">
            <Terminal className="h-3.5 w-3.5" />
            Agent integrations · August 2, 2026 · Independent Guide
          </div>

          <article>
            <h1 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4 leading-tight">
              Reasonix: The DeepSeek-Native Coding Agent That Lives in Your Terminal
            </h1>

            <p className="text-lg text-slate-600 mb-2">
              By the Deep Seek AI editorial desk · August 2, 2026 · 7 min read
            </p>
            <p className="text-base text-slate-500 mb-8 italic">
              DeepSeek's agent-integrations documentation now lists <strong>Reasonix</strong> — a
              coding agent written for DeepSeek's own API rather than bolted onto an
              OpenAI-compatible shim. One command, <code>npx reasonix code</code>, and you have an
              agent in your project directory running on <strong>DeepSeek-V4-Flash</strong> by
              default. Here is what it is, how to set it up, and what a session actually costs.
            </p>

            <Card className="mb-8 border-blue-200 bg-blue-50/60">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-3 mt-0">The 60-second version</h2>
                <ul className="text-sm text-slate-800 space-y-2 list-disc pl-5">
                  <li>
                    <strong>Terminal-first, DeepSeek-first.</strong> Reasonix talks to{" "}
                    <code>api.deepseek.com</code> directly — no translation layer between the agent
                    and the model.
                  </li>
                  <li>
                    <strong>Three-step setup.</strong> Node.js 20.10+, an API key from the DeepSeek
                    Platform, then <code>npx reasonix code</code> in your project folder.
                  </li>
                  <li>
                    <strong>No global install, no env var.</strong> A first-run wizard stores your
                    key in <code>~/.reasonix/config.json</code>.
                  </li>
                  <li>
                    <strong>Flash-first cost control.</strong> V4-Flash is the default;{" "}
                    <code>/pro</code> arms V4-Pro for one turn, <code>/preset max</code> for the
                    whole session.
                  </li>
                  <li>
                    <strong>Cache-first loop and automatic tool-call repair</strong> are the two
                    design choices that make a long agent session survivable — both technically and
                    financially.
                  </li>
                </ul>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Why "DeepSeek-native" is not just marketing
            </h2>
            <p className="text-slate-700 mb-4">
              Almost every popular coding agent was designed for a different vendor's API and reaches
              DeepSeek through the OpenAI-compatible endpoint. That works, but it flattens everything
              DeepSeek does differently. Two things get lost in particular:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-6">
              <li>
                <strong>Context caching.</strong> DeepSeek bills cache hits at a fraction of the
                cache-miss rate. An agent that does not deliberately structure its prompts to hit the
                cache throws that discount away on every turn. Reasonix is built around a
                <em> cache-first loop</em>, which is exactly the right optimisation target for an
                agent that re-sends a large, mostly-identical project context dozens of times.
              </li>
              <li>
                <strong>Tool-call behaviour.</strong> Model-specific quirks in function calling are
                usually patched with retries in generic agents. Reasonix advertises{" "}
                <em>automatic tool-call repair</em> — it fixes malformed calls instead of burning a
                whole turn on them.
              </li>
            </ul>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Setup: three steps, about two minutes
            </h2>

            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-2">1. Install Node.js</h3>
            <p className="text-slate-700 mb-4">
              You need <strong>Node.js 20.10 or newer</strong>. On Windows you also need{" "}
              <a
                href="https://git-scm.com/download/win"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0066FF] underline"
              >
                Git for Windows
              </a>
              , since the agent shells out to git for its file operations.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-2">2. Get a DeepSeek API key</h3>
            <p className="text-slate-700 mb-4">
              Create a key at{" "}
              <a
                href="https://platform.deepseek.com/api_keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0066FF] underline"
              >
                platform.deepseek.com/api_keys
              </a>
              . You do <em>not</em> need to export it: the first run of Reasonix prompts for the key
              through a built-in wizard and writes it to <code>~/.reasonix/config.json</code>. That
              is convenient, and it is also worth knowing — that file is a plaintext credential on
              your disk, so treat it like an SSH key and keep it out of any synced folder or repo.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-2">3. Run it in your project</h3>
            <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm mb-4">
              <code>{`cd /path/to/my-project
npx reasonix code`}</code>
            </pre>
            <p className="text-slate-700 mb-6">
              <code>npx</code> means no global install and no version pinned in your toolchain — you
              get the current release each time. The agent scopes itself to the directory you launch
              it in, so launch it at the repo root you actually want it editing.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Model control: Flash by default, Pro on demand
            </h2>
            <p className="text-slate-700 mb-4">
              This is the part that matters most for your bill. Reasonix defaults to{" "}
              <strong>DeepSeek-V4-Flash</strong> for cost-efficient iteration and gives you explicit
              escalation instead of silently spending Pro money:
            </p>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm border border-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 border-b border-slate-200 font-semibold">Command</th>
                    <th className="text-left p-3 border-b border-slate-200 font-semibold">Effect</th>
                  </tr>
                </thead>
                <tbody>
                  {slashCommands.map((c) => (
                    <tr key={c.cmd} className="border-b border-slate-100">
                      <td className="p-3 font-mono text-[#0066FF]">{c.cmd}</td>
                      <td className="p-3 text-slate-700">{c.what}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-slate-700 mb-4">
              The practical workflow: stay on Flash for exploration, file reading, mechanical edits
              and test loops. Reach for <code>/pro</code> on the single turn where the reasoning is
              genuinely hard — an architectural refactor, a subtle concurrency bug, a migration plan.
              Reserve <code>/preset max</code> for sessions where every turn is that hard.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              What a session costs
            </h2>
            <p className="text-slate-700 mb-4">
              Current DeepSeek API rates per 1M tokens, straight from our{" "}
              <Link to="/pricing" className="text-[#0066FF] underline">
                pricing reference
              </Link>
              :
            </p>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm border border-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 border-b border-slate-200 font-semibold">Model</th>
                    <th className="text-left p-3 border-b border-slate-200 font-semibold">Input (cache miss)</th>
                    <th className="text-left p-3 border-b border-slate-200 font-semibold">Input (cache hit)</th>
                    <th className="text-left p-3 border-b border-slate-200 font-semibold">Output</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100">
                    <td className="p-3 font-medium">DeepSeek-V4-Flash (default)</td>
                    <td className="p-3">${flash.inputCacheMiss}</td>
                    <td className="p-3">${flash.inputCacheHit}</td>
                    <td className="p-3">${flash.output}</td>
                  </tr>
                  <tr>
                    <td className="p-3 font-medium">DeepSeek-V4-Pro (/pro)</td>
                    <td className="p-3">${pro.inputCacheMiss}</td>
                    <td className="p-3">${pro.inputCacheHit}</td>
                    <td className="p-3">${pro.output}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-slate-700 mb-6">
              The cache-hit column is the whole game for a terminal agent. Your project context is
              re-sent on every turn, so a cache-first agent pays the miss rate roughly once and the
              hit rate thereafter. {surchargeSentence}
            </p>

            <Card className="mb-8 border-amber-200 bg-amber-50">
              <CardContent className="p-6 flex gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-slate-900 mb-2 mt-0">Two things to check before you let it run</h3>
                  <ul className="text-sm text-slate-800 space-y-2 list-disc pl-5">
                    <li>
                      <strong>Commit first.</strong> A terminal agent edits real files. Start from a
                      clean git tree so every change it makes is reviewable with{" "}
                      <code>git diff</code>.
                    </li>
                    <li>
                      <strong>Reasonix is third-party.</strong> DeepSeek documents it as a supported
                      integration; it is not a DeepSeek product. You are handing an independent tool
                      an API key and write access to your source, so read the project's own docs
                      before pointing it at anything sensitive.
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Where it fits next to the rest of the DeepSeek agent story
            </h2>
            <p className="text-slate-700 mb-6">
              This lands right after the{" "}
              <Link to="/blog/deepseek-v4-flash-ga-agent-benchmarks" className="text-[#0066FF] underline">
                official V4-Flash release on July 31, 2026
              </Link>
              , which was explicitly an <em>agent</em> upgrade: fresh post-training, native Responses
              API support and Codex adaptation, with agent benchmark scores passing V4-Pro-Preview.
              A Flash-first terminal agent only makes sense because the cheap tier is now genuinely
              good at multi-step tool use. If you want the model-side detail first, start with our{" "}
              <Link to="/deepseek-v4" className="text-[#0066FF] underline">
                DeepSeek V4 overview
              </Link>{" "}
              and the{" "}
              <Link to="/deepseek-api" className="text-[#0066FF] underline">
                DeepSeek API guide
              </Link>
              .
            </p>

            <div className="flex flex-wrap gap-3 mb-10">
              <Button asChild className="bg-[#0066FF] hover:bg-[#0052cc]">
                <Link to="/deepseek-api">
                  <Zap className="h-4 w-4 mr-2" />
                  Explore the DeepSeek V4 API
                </Link>
              </Button>
              <Button asChild variant="outline">
                <a
                  href="https://api-docs.deepseek.com/quick_start/agent_integrations/reasonix/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Official Reasonix setup docs
                </a>
              </Button>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Frequently asked questions
            </h2>
            <div className="space-y-5 mb-10">
              {faqSchema.mainEntity.map((q: any) => (
                <div key={q.name} className="border-b border-slate-200 pb-4">
                  <h3 className="font-semibold text-slate-900 mb-2">{q.name}</h3>
                  <p className="text-slate-700 text-sm">{q.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>

            <p className="text-sm text-slate-500 mb-10">
              Source: DeepSeek's official agent-integrations documentation for Reasonix (
              <a
                href="https://api-docs.deepseek.com/quick_start/agent_integrations/reasonix/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0066FF] underline"
              >
                api-docs.deepseek.com
              </a>
              ). This is an independent fan-run guide and is not affiliated with DeepSeek or with the
              Reasonix project.
            </p>

            <div className="border-t border-slate-200 pt-8">
              <h2 className="text-2xl font-bold mb-6">Related reading</h2>
              <RelatedLinks />
            </div>
          </article>

          <NavigationButtons />
        </main>
      </div>
    </>
  );
};

export default DeepseekReasonix;
